import React, { useContext, useState } from 'react'
import { Button, ButtonModel, IconRegularClose, Spinner } from '@notino/react-styleguide'
import { useForm } from 'react-hook-form'

// types
import { CancelReservationAnswer, CancelReservationForm, ConfigResponse, PatchCancelCalendarEventsReservationBody } from '../../../../types/types'
import { INFO_LINE_TYPE } from '../../../../atoms/InfoLine/types'
import { RadioButtonOption } from '../../../../formFields/RadioButtonsFormField/types'

// utils
import { VALIDATION_MAX_LENGTH } from '../../../../utils/enums'
import { AppContext } from '../../../../utils/appProvider'
import { PATCH_RESERVATION_CANCEL_ERROR_KEYS_MAP } from '../../../../utils/clientErrorValidations'

// components
import HookFormField from '../../../../formFields/HookFormField'
import TextAreaFormField from '../../../../formFields/TextAreaFormField/TextAreaFormField'
import InfoLine from '../../../../atoms/InfoLine/InfoLine'
import RadioButtonsFormField from '../../../../formFields/RadioButtonsFormField/RadioButtonsFormField'
import Modal, { ModalModel } from '../../../../atoms/Modal/Modal'

// hooks
import useMessages from '../../../../hooks/useMessages'

// styles
import * as SC from './CancelReservationModalStyles'

type Props = {
	isOpen: boolean
	onClose: () => void
	reservationID: string
	onSubmitSuccess: () => void
	queryParamsToken: string | undefined
	reservationCancellationQuickFeedbackAnswers: ConfigResponse['reservationCancellationQuickFeedbackAnswers']
}

const CancelReservationModal = (props: Props) => {
	const { messages } = useMessages()
	const { isOpen, onClose, reservationID, onSubmitSuccess, reservationCancellationQuickFeedbackAnswers, queryParamsToken } = props

	const { apiBrowser, isAuthorized } = useContext(AppContext)

	const [patchGeneralError, setPatchGeneralError] = useState('')

	const {
		control,
		handleSubmit,
		watch,
		setError,
		formState: { isSubmitting }
	} = useForm<CancelReservationForm>({
		defaultValues: {
			quickFeedbackAnswer: undefined,
			text: ''
		}
	})

	const { quickFeedbackAnswer, text } = watch()

	let hasAnswer = false

	if (quickFeedbackAnswer && quickFeedbackAnswer !== 'OWN_MESSAGE') {
		hasAnswer = true
	} else if (quickFeedbackAnswer === 'OWN_MESSAGE' && text) {
		hasAnswer = true
	}

	const disabledSubmitButton = isSubmitting || !hasAnswer
	const disabledGoBackButton = isSubmitting

	const handleSubmitCancelReservationForm = async (values: CancelReservationForm) => {
		setPatchGeneralError('')

		try {
			// NOTE: text is allowed to be type string | null | undefined according to swagger schema
			// but there is a problem with our library, which converts swagger schema to TS types, which can't convert it correctly
			// so we need to cast is as any in order to suppress TS error
			const requestBody: PatchCancelCalendarEventsReservationBody = {
				reservationCancellation: {
					quickFeedbackAnswers: [values.quickFeedbackAnswer],
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					text: (values.text || null) as any
				}
			}

			await apiBrowser.b2c.patchCalendarEventsReservationCancel(reservationID, requestBody, {
				formErrors: { setError, errorsMap: PATCH_RESERVATION_CANCEL_ERROR_KEYS_MAP },
				// NOTE: token in query parameters are only for guest users, requests for authorized users already have token in headers, which is handled on the app level axios settings
				headers: isAuthorized
					? undefined
					: {
							Authorization: `Bearer ${queryParamsToken}`
						}
			})

			onSubmitSuccess()
		} catch {
			// Empty block
		}
	}

	const feedbackOptions: RadioButtonOption<CancelReservationAnswer>[] = reservationCancellationQuickFeedbackAnswers.map((answer) => ({
		id: answer.code,
		label: answer.name
	}))

	return (
		<Modal
			show={isOpen}
			layout={'fullScreenOnMobileCustom'}
			hideDefaultCloseButton
			desktopMaxWidth={360}
			onClose={onClose}
			size={ModalModel.ModalSizes.default}
			noBorders
			ariaLabel={messages['Reservation cancellation']}
		>
			<SC.ModalContent>
				<SC.Form onSubmit={handleSubmit(handleSubmitCancelReservationForm)}>
					<SC.Header>
						<SC.HeaderTitle>{messages['Reservation cancellation']}</SC.HeaderTitle>
						<SC.CloseButton type={'button'} onClick={onClose} aria-label={messages['Close modal']}>
							<IconRegularClose color={'icon.primary'} aria-hidden />
						</SC.CloseButton>
					</SC.Header>
					<SC.Description>
						{messages['Once you cancel a reservation, it cannot be renewed. The salon will receive a cancellation notification.']}
					</SC.Description>
					{patchGeneralError && <InfoLine type={INFO_LINE_TYPE.ERROR} message={patchGeneralError} />}
					<SC.Main>
						<HookFormField
							control={control}
							name={'quickFeedbackAnswer'}
							component={RadioButtonsFormField<CancelReservationAnswer>}
							label={<SC.FeedbackAnswersLabel>{messages['What’s the reason for your cancellation?']}</SC.FeedbackAnswersLabel>}
							options={feedbackOptions}
							required
						/>
						{quickFeedbackAnswer === 'OWN_MESSAGE' && (
							<HookFormField
								control={control}
								name={'text'}
								component={TextAreaFormField}
								label={messages['Please describe the reason']}
								labelScreenReaderOnly
								placeholder={messages['Please describe the reason']}
								maxLength={VALIDATION_MAX_LENGTH.LENGTH_160}
								autoSize={{
									minRows: 3
								}}
							/>
						)}
					</SC.Main>
					<SC.Footer>
						<Button fullWidth type={'submit'} buttonStyle={ButtonModel.Styles.primary} disabled={disabledSubmitButton}>
							{isSubmitting ? <Spinner size={16} color={'icon.disabled'} /> : messages['Cancel reservation']}
						</Button>
						<Button fullWidth type={'button'} buttonStyle={ButtonModel.Styles.secondary} disabled={disabledGoBackButton} onClick={onClose}>
							{messages['Go back']}
						</Button>
					</SC.Footer>
				</SC.Form>
			</SC.ModalContent>
		</Modal>
	)
}

export default CancelReservationModal
